import { CalendarOutlined, CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, DatePicker, Dropdown, Space, theme, Typography } from 'antd';
import { type RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';
import PropTypes from 'prop-types';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';

const displayFormat = 'MMM D, YY';

const StyledButton = styled(Button)`
  &:focus-visible {
    outline-width: 0 !important;
    outline-offset: 0 !important;
    box-shadow: 0 0 0 2px rgb(255 5 145 / 10%);
  }
`;

export interface RangePresetType {
  label: string;
  value: Dayjs[];
  key: string;
}

export const getRangePresets = (): RangePresetType[] => {
  const startOfThisMonth = dayjs().startOf('M');

  // next month data range
  const startOfNextMonth = startOfThisMonth.add(1, 'M');
  const endOfNextMonth = startOfNextMonth.endOf('M');

  // last month data range
  const startOfLastMonth = startOfThisMonth.subtract(1, 'M');
  const endOfLastMonth = startOfLastMonth.endOf('M');

  return [
    {
      label: 'Today',
      value: [dayjs(), dayjs().endOf('d')],
      key: 'd',
    },
    {
      label: 'Tomorrow',
      value: [dayjs().add(1, 'd'), dayjs().add(1, 'd').endOf('d')],
      key: 't',
    },
    {
      label: 'This week',
      value: [dayjs().startOf('w'), dayjs().endOf('w')],
      key: 'w',
    },
    {
      label: 'Last week',
      value: [dayjs().startOf('w').subtract(1, 'w'), dayjs().endOf('w').subtract(1, 'w')],
      key: '-w',
    },
    {
      label: 'Next week',
      value: [dayjs().startOf('w').add(1, 'w'), dayjs().endOf('w').add(1, 'w')],
      key: '+w',
    },
    {
      label: 'This month',
      value: [dayjs().startOf('M'), dayjs().endOf('M')],
      key: 'm',
    },
    {
      label: 'Last month',
      value: [startOfLastMonth, endOfLastMonth],
      key: '-m',
    },
    {
      label: 'Next month',
      value: [startOfNextMonth, endOfNextMonth],
      key: '+m',
    },
  ];
};

type DateRangePickerProps = {
  autoFocus: boolean;
  defaultOpen: boolean;
  value?: [Dayjs, Dayjs];
  title: string;
  onChange: (value?: [Dayjs, Dayjs]) => void;
  customRangePreset?: RangePresetType[];
  customDisplayFormat?: string;
  showOnlyDateRange?: boolean;
} & RangePickerProps;

const DateRangePicker = (props: DateRangePickerProps): ReactNode => {
  const defaultButtonLabel = 'All time';
  const {
    autoFocus = false,
    defaultOpen = false,
    value,
    onChange,
    title,
    customRangePreset,
    customDisplayFormat,
    showOnlyDateRange = false,
    ...datePickerProps
  } = props;
  const [isFocused, setFocus] = useState(autoFocus);
  const [isPickerOpened, setPickerOpen] = useState(defaultOpen);
  const [isButtonHovered, setButtonHovered] = useState(false);
  const pickerRef = useRef<any | null>(null);
  // const buttonRef = useRef<HTMLElement | null>(null);
  const [buttonLabel, setButtonLabel] = useState(defaultButtonLabel);
  const isSelected = useRef(value != null);
  const { token } = theme.useToken();
  const presets = customRangePreset ?? getRangePresets();
  // useEffect(() => {
  //   if (defaultOpen) {
  //     buttonRef.current?.click();
  //   }
  // }, []);

  useEffect(() => {
    isSelected.current = value != null;
    setButtonLabel(
      value == null
        ? defaultButtonLabel
        : !showOnlyDateRange
        ? presets.find((item) => JSON.stringify(item.value) === JSON.stringify(value))?.label ?? ''
        : value?.map((date) => date.format(customDisplayFormat ?? displayFormat)).join(' ~ ')
    );
  }, [value]);

  const onRangeChange = (
    dates: Dayjs[] | undefined,
    clickedItem: RangePresetType | undefined
  ): void => {
    setButtonLabel(
      clickedItem && !showOnlyDateRange
        ? clickedItem.label
        : value
        ? value.map((date) => date.format(customDisplayFormat ?? displayFormat)).join(' ~ ')
        : ' '
    );
    isSelected.current = !!dates;
    onChange((dates?.map((date) => date.startOf('d')) as [Dayjs, Dayjs]) ?? undefined);
    pickerRef.current?.blur();
    setFocus(false);
  };

  const openPicker = (): void => {
    // setFocus(true);
    pickerRef.current?.focus();
    setPickerOpen(true);
    // document.activeElement?.parentElement?.parentElement?.click();
  };

  return (
    <div style={{ position: 'relative' }}>
      <Dropdown
        disabled={datePickerProps.disabled as boolean}
        menu={{
          items: (presets as any[]).concat([
            { type: 'divider' },
            {
              icon: <CalendarOutlined />,
              label: 'Custom date range',
              key: 'custom',
            },
          ]),
          onClick: ({ key }) => {
            // getting a fresh range preset prevents getting stale ranges during renders
            const actualPresets = customRangePreset ?? getRangePresets();
            const clickedItem = actualPresets.find((item) => item.key === key);
            key === 'custom' ? openPicker() : onRangeChange(clickedItem?.value, clickedItem);
          },
          selectedKeys: value
            ? [
                presets.find((item) => JSON.stringify(item.value) === JSON.stringify(value))?.key ??
                  '',
              ]
            : [],
        }}
        onOpenChange={(nextOpen) => {
          setFocus(nextOpen);
        }}
        trigger={['click']}
      >
        <StyledButton
          // ref={(ref) => (buttonRef.current = ref)}
          onFocus={() => {
            setFocus(true);
          }}
          onBlur={() => {
            setFocus(false);
          }}
          onMouseEnter={() => {
            setButtonHovered(true);
          }}
          onMouseLeave={() => {
            setButtonHovered(false);
          }}
          style={{
            paddingLeft: token.paddingXXS,
            paddingRight: token.paddingXXS,
            borderColor: isButtonHovered
              ? token.colorPrimary
              : isFocused
              ? token.colorPrimaryHover
              : isSelected.current
              ? token.colorPrimaryBg
              : undefined,
            // background: isSelected.current ? token.colorPrimaryBg : undefined,
          }}
        >
          <Space
            style={{
              marginRight: 3,
              paddingLeft: token.paddingXS,
              paddingRight: token.paddingXS,
            }}
          >
            <Typography.Text type="secondary">{title}</Typography.Text>
            <Typography.Text strong style={isSelected.current ? { color: token.colorPrimary } : {}}>
              {buttonLabel}
            </Typography.Text>
            <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextDisabled }}>
              {isButtonHovered && value != null ? (
                <CloseCircleFilled
                  onClick={(e) => {
                    pickerRef.current?.blur();
                    setFocus(false);
                    isSelected.current = false;
                    onChange(undefined);
                    setButtonHovered(false);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              ) : (
                <DownOutlined />
              )}
            </Typography.Text>
          </Space>
        </StyledButton>
      </Dropdown>
      {(value || isPickerOpened) && (
        <DatePicker.RangePicker
          // autoFocus={autoFocus}
          open={isPickerOpened}
          style={{
            overflow: 'hidden',
            zIndex: -1,
            position: 'absolute',
            paddingLeft: token.paddingXXS,
            paddingRight: token.paddingXXS,
            left: token.paddingXXS + 2,
            width: `calc(100% - ${token.paddingXS + 2}px)`,
            border: '0',
          }}
          ref={(ref) => (pickerRef.current = ref)}
          onChange={(dates) => {
            onRangeChange(
              (dates as Dayjs[]).map((date) => date),
              undefined
            );
          }}
          onOpenChange={(nextOpen) => {
            setFocus(nextOpen);
            nextOpen && pickerRef.current?.blur();
            setPickerOpen(nextOpen);
          }}
          {...datePickerProps}
        />
      )}
    </div>
  );
};

DateRangePicker.propTypes = {
  autoFocus: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  title: PropTypes.string.isRequired,
};

DateRangePicker.defaultProps = {
  autoFocus: false,
  defaultOpen: false,
  value: null,
  onChange: () => {},
};

export default DateRangePicker;
